import { computed, shallowRef, useHost } from "vue";
import { useSharedState } from "composables/useSharedState";
import { closestAcrossShadowBoundaries } from "@/runtime/shared/shadow-dom";
import { createEventDispatcher } from "@/runtime/shared/events";
import type { TabTriggerProps } from "./tab.types";
import type { Colors, Variants } from "@/runtime/types";
import { getTabActiveValue } from "./useTab";
import { tabTriggerButtonClasses, tabTriggerContentClasses, tabTriggerDescriptionClasses } from "./tab.styles";

export interface UseTabTriggerOptions {
  props: Readonly<TabTriggerProps>;
}

export const useTabTrigger = (options: UseTabTriggerOptions) => {
  const { props } = options;
  const host = useHost();
  const state = useSharedState(props);

  // Cache DOM lookup during setup — never inside computed.
  const tabElement = shallowRef<Element | null>(null);
  if (host) {
    tabElement.value = closestAcrossShadowBoundaries(host, "e-tab");
  }

  // Dispatch selection on the parent <e-tab>: `update:value` non-bubbling for
  // the parent's listener, `change` bubbles + composed for outside consumers.
  const dispatchOnTab = createEventDispatcher(tabElement.value as Element | null);

  const isActive = computed<true | undefined>(() => {
    const el = tabElement.value;
    if (!el) return undefined;
    const activeValue = getTabActiveValue(el);
    return !!activeValue && activeValue.value === state.value?.value ? true : undefined;
  });

  const isDisabled = computed<boolean>(() => !!state.disabled?.value);

  // Pre-extract to avoid proxy overhead in dependent computeds.
  const color = computed<Colors>(() => state.color?.value ?? "neutral");
  const variant = computed(() => state.variant?.value);

  // Active → configured variant; inactive → ghost so the color tints hover only.
  const activeVariant = computed<Exclude<Variants, "link">>(() =>
    isActive.value ? (variant.value ?? "subtle") : "ghost",
  );

  const onClick = (): void => {
    if (isDisabled.value) return;
    const value = state.value?.value;
    if (!tabElement.value || !value) return;
    dispatchOnTab("update:value", value);
    dispatchOnTab("change", value);
  };

  const hasDescription = computed(() => Boolean(props.description));
  const buttonClass = computed(() =>
    tabTriggerButtonClasses({ hasDescription: hasDescription.value, size: props.size }),
  );
  const contentClass = computed(() => tabTriggerContentClasses());
  const descriptionClass = computed(() => tabTriggerDescriptionClasses());

  return {
    isActive,
    isDisabled,
    color,
    activeVariant,
    onClick,
    hasDescription,
    buttonClass,
    contentClass,
    descriptionClass,
  };
};
